// The input mode in use (inputMode.ts) at run time: the Settings choice resolved against what this browser reports,
// followed live (the setting changes, or a tablet gets a mouse and its media queries flip). It also sets a class
// on <html> (input-touch / input-desktop) for the CSS, and exposes a React hook.
import {useSyncExternalStore} from "react";
import {PlayerSettings} from "./settings";
import {DeviceCaps, InputMode, detectCaps, resolveInputMode} from "./inputMode";

let caps: DeviceCaps = detectCaps();
let mode: InputMode = resolveInputMode(PlayerSettings.get().inputMode, caps);
const listeners = new Set<(m: InputMode) => void>();

function applyClass() {
    if (typeof document === "undefined" || !document.documentElement) return;
    const el = document.documentElement;
    el.classList.toggle("input-touch", mode === "touch");
    el.classList.toggle("input-desktop", mode === "desktop");
}

function refresh() {
    const next = resolveInputMode(PlayerSettings.get().inputMode, caps);
    if (next === mode) return;
    mode = next;
    applyClass();
    listeners.forEach(l => l(mode));
}

applyClass();
PlayerSettings.subscribe(refresh);
function onDeviceChange() { caps = detectCaps(); refresh(); }
if (typeof window !== "undefined" && window.matchMedia) {
    for (const q of ["(pointer: coarse)", "(hover: none)"]) {
        try {
            const m = window.matchMedia(q);
            if (m && m.addEventListener) m.addEventListener("change", onDeviceChange);
        } catch {}
    }
}

export const InputModeState = {
    get: (): InputMode => mode,
    isTouch: (): boolean => mode === "touch",
    /** what Auto detects on this device (for the Settings panel) */
    detected: (): InputMode => resolveInputMode("auto", caps),
    subscribe(l: (m: InputMode) => void) {
        listeners.add(l);
        return () => { listeners.delete(l); };
    },
};

export function useInputMode(): InputMode {
    return useSyncExternalStore(InputModeState.subscribe, InputModeState.get);
}
